import { apiQueryKeys } from '@/services/apis/apiQueryKeys';
import { me } from '@/services/apis/auth/me';
import { useGlobalStore } from '@/stores/store';
import { checkIfLocationAssociateWithNbn } from '@/utils/address-search';
import { hasAtLeastOneTheOnePlan, isLegacyUser } from '@/utils/auth';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { useRemoveBroadbandOffer } from './use-remove-broadband-offer';
import { useShallow } from 'zustand/react/shallow';
import { getGlobalStore } from '@/utils/store';

export function useAuthDetails({ isEnabled = false }: { isEnabled: boolean }) {
  const queryClient = useQueryClient();
  const {
    setLoginCredentials,
    setIsAuthSheetLoading,
    setIsAuthSheetOpen,
    setLogout,
    setFeedbackCardRecentSearchVisible,
    setFeedbackCardAddressSearchVisible,
  } = getGlobalStore();

  const { latestSelectedAddresses, customerId, loginAccessToken } =
    useGlobalStore(
      useShallow((store) => ({
        latestSelectedAddresses: store.addressStore.latestSelectedAddresses,
        customerId: store.authStore.customerId,
        loginAccessToken: store.authStore.loginAccessToken,
      }))
    );

  const latestSelectedAddress =
    latestSelectedAddresses.length > 0 ? latestSelectedAddresses[0] : null;

  const removeBroadbandOffer = useRemoveBroadbandOffer();

  const closeAuthSheet = useCallback(() => {
    setIsAuthSheetOpen(false);
    setIsAuthSheetLoading(false);
  }, [setIsAuthSheetLoading, setIsAuthSheetOpen]);

  const navigate = useNavigate();
  const { data: meData, isError: isErrorMe } = useQuery({
    queryKey: apiQueryKeys.me(customerId),
    queryFn: isEnabled && customerId ? () => me(customerId) : skipToken,
    refetchOnWindowFocus: false,
  });

  const checkIfUserHasNbnAssociatedLocation = useCallback(() => {
    if (!meData) return;
    checkIfLocationAssociateWithNbn({
      address: latestSelectedAddress,
      services: meData.services,
      callback: () => {
        setFeedbackCardRecentSearchVisible('already-has-service');
        setFeedbackCardAddressSearchVisible('already-has-service');
        removeBroadbandOffer();
      },
    });
  }, [
    meData,
    latestSelectedAddress,
    setFeedbackCardAddressSearchVisible,
    setFeedbackCardRecentSearchVisible,
    removeBroadbandOffer,
  ]);

  useEffect(() => {
    //if me endpoint throws an error logout.
    if (isErrorMe) {
      queryClient.removeQueries({
        queryKey: apiQueryKeys.me(customerId),
        exact: true,
      });
      setLogout();
      return;
    }
    if (!meData) return;
    setLoginCredentials(meData);

    // Redirect to legacy portal if it doesn't contain new broadbandPlanIds.
    if (isLegacyUser({ services: meData.services })) {
      //redirect to legacy portal
      window.location.href = `${import.meta.env.VITE_LEGACY_PORTAL_URL}?token=${loginAccessToken}`;
      return;
    }

    checkIfUserHasNbnAssociatedLocation();

    // We are 100% sure user is not legacy, we can check if it has NbnBroadband then redirect to get exetel app
    if (hasAtLeastOneTheOnePlan({ services: meData.services })) {
      navigate({ to: '/get-exetel-app' });
    }

    //If none of the checks are true we can just close the sheet.
    closeAuthSheet();
  }, [
    meData,
    isErrorMe,
    customerId,
    queryClient,
    loginAccessToken,
    setLoginCredentials,
    checkIfUserHasNbnAssociatedLocation,
    setLogout,
    navigate,
    closeAuthSheet,
  ]);
}
